import {
  buildIcs,
  CALENDAR_PROVIDERS,
  icsFileName,
  isUsableCalendarEvent,
  type CalendarEvent,
} from '@auctisync/shared';
import { useEffect, useRef, useState } from 'react';

/**
 * "Add to calendar", offering the calendar the person actually uses.
 *
 * 🔴 WHY A MENU AND NOT A BUTTON. Jim, 2026-09-18: *"We need to add 'Add to
 * calendar' and include options for Apple, Google, and other major calendars."*
 * A lone `.ics` download is standards-correct and a poor experience for the
 * majority living in Google Calendar or Outlook on the web, where a file is a
 * detour through the filesystem. The link-out providers get a URL; Apple and
 * "Other" get the file.
 *
 * 🔴 IT RENDERS NOTHING WITHOUT A REAL DATE, and that is the whole contract
 * rather than defensive coding. The platform holds most collection times as
 * PROSE — "Sat 9–4" cannot say which Saturday — and a calendar entry on the
 * wrong Saturday is worse than no button, because the buyer now believes they
 * have a reminder. The caller passes `null` when there is no scheduled time and
 * this disappears.
 */
export function AddToCalendar({
  event,
  label = 'Add to calendar',
  className = 'w-btn w-btn-ghost w-btn-sm',
}: {
  event: CalendarEvent | null;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // A menu that survives a click elsewhere on the page reads as a stuck
  // overlay. Escape closes it too, because a keyboard user has no other way out.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current !== null && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (event === null || !isUsableCalendarEvent(event)) return null;

  /**
   * Hand over the `.ics` as a Blob rather than a `data:` URL.
   *
   * Safari refuses to navigate to a `data:` URL from a link, and it is Safari's
   * users who most want the Apple Calendar entry. A Blob URL is revoked on the
   * next tick — long enough for the download to start, short enough not to
   * leak a file handle per click.
   */
  const download = (contents: string) => {
    const blob = new Blob([contents], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = icsFileName(event);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {label}
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            zIndex: 40,
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: 200,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 'var(--r-tile, 8px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            padding: 4,
          }}
        >
          {CALENDAR_PROVIDERS.map((provider) => {
            const href = provider.url(event);
            if (href === '') return null;

            if (provider.kind === 'download') {
              return (
                <button
                  key={provider.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    download(buildIcs(event));
                    setOpen(false);
                  }}
                  style={menuItemStyle}
                >
                  {provider.label}
                </button>
              );
            }

            return (
              <a
                key={provider.id}
                role="menuitem"
                href={href}
                target="_blank"
                // `noopener` matters here specifically: these are third-party
                // origins opened from a page the bidder is signed into.
                rel="noreferrer noopener"
                onClick={() => {
                  setOpen(false);
                }}
                style={{ ...menuItemStyle, display: 'block', textDecoration: 'none' }}
              >
                {provider.label}
              </a>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 14,
  color: 'var(--text, #111)',
  background: 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
